// Catalog page — the full "browse everything you can request" inventory.
//
// Title + (search · Catalog filter) row, then one section per access catalog:
// a header (icon · name · count) + description, and a grid of app cards
// (logo · name · description · "N items"). Clicking a card opens the Request
// Access modal focused on that catalog item. Same access_v2 data as the bulk
// Request Access flow.

import { Box, Flex, Heading, Icon, SimpleGrid, Text, VStack } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { TbBook2, TbCheck, TbSearchOff } from "react-icons/tb";

import { getIcon } from "components/CatalogIcon";
import AppLogo from "components/ds/AppLogo";
import SearchInput from "components/ds/SearchInput";
import FilterDropdown from "components/RequestAccess/FilterDropdown";
import { RequestAccessModal } from "components/RequestAccess";
import {
  MF_ACCESS,
  type MFAccessCatalog,
  type MFCatalogItem,
} from "state/members/access_v2/catalogs";
import {
  activeAccessResourceIds,
  itemHasActiveAccess,
} from "state/members/access_v2/catalogAccess";

interface RequestTarget {
  catalogId: string;
  itemId: string;
}

// Gray count/meta pill (e.g. section "7", card "1 item").
const Pill = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    bg="neutral.100"
    color="neutral.700"
    px="8px"
    py="2px"
    borderRadius="full"
    fontSize="xs"
    fontWeight={600}
    lineHeight="1.3"
    whiteSpace="nowrap"
  >
    {children}
  </Box>
);

const AppCard = ({
  item,
  onClick,
  hasAccess,
}: {
  item: MFCatalogItem;
  onClick: () => void;
  /** The member already holds active access to one of this app's resources. */
  hasAccess: boolean;
}) => {
  const count = item.catalogItemSelectableResources.length;
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="flex"
      flexDirection="column"
      w="full"
      textAlign="left"
      bg="neutral.0"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="neutral.200"
      borderRadius="component.2xl" // 12px
      p="20px"
      cursor="pointer"
      fontFamily="inherit"
      color="inherit"
      transition="border-color 120ms, box-shadow 120ms, transform 120ms"
      _hover={{ borderColor: "neutral.300", boxShadow: "elevLow" }}
      _active={{ transform: "scale(0.99)" }}
      _focusVisible={{ outline: "none", borderColor: "p.400", boxShadow: "focusRingSubtle" }}
    >
      <Flex gap="md" align="center">
        <AppLogo size="lg" name={item.name} logoUrl={item.imageUrl || undefined} />
        <Text fontSize="lg" fontWeight={600} color="neutral.950" lineHeight="1.2" noOfLines={2}>
          {item.name}
        </Text>
      </Flex>
      {item.description && (
        <Text fontSize="md" color="neutral.700" mt="12px" lineHeight="1.4" noOfLines={2}>
          {item.description}
        </Text>
      )}
      <Flex mt="auto" pt="14px" gap="sm" align="center" wrap="wrap">
        <Pill>
          {count} item{count === 1 ? "" : "s"}
        </Pill>
        {hasAccess && (
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            gap="4px"
            bg="success.50"
            color="success.700"
            px="8px"
            py="2px"
            borderRadius="full"
            fontSize="xs"
            fontWeight={600}
            lineHeight="1.3"
            whiteSpace="nowrap"
          >
            <Icon as={TbCheck} boxSize="12px" strokeWidth={3} />
            You have access
          </Box>
        )}
      </Flex>
    </Box>
  );
};

const CatalogSection = ({
  catalog,
  items,
  onRequest,
  activeResourceIds,
}: {
  catalog: MFAccessCatalog;
  items: MFCatalogItem[];
  onRequest: (target: RequestTarget) => void;
  activeResourceIds: Set<string>;
}) => (
  <Box>
    <Flex align="center" gap="sm">
      <Flex
        w="32px"
        h="32px"
        align="center"
        justify="center"
        bg="neutral.100"
        color="neutral.700"
        borderRadius="component.lg" // 8px
        flexShrink={0}
      >
        <Icon as={getIcon(catalog.icon)} boxSize="18px" />
      </Flex>
      <Text fontSize="lg" fontWeight={600} color="neutral.950">
        {catalog.name}
      </Text>
      <Pill>{items.length}</Pill>
    </Flex>
    {catalog.description && (
      <Text fontSize="md" color="neutral.700" mt="sm">
        {catalog.description}
      </Text>
    )}
    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing="lg" mt="lg">
      {items.map((item) => (
        <AppCard
          key={item.id}
          item={item}
          onClick={() => onRequest({ catalogId: catalog.id, itemId: item.id })}
          hasAccess={itemHasActiveAccess(item, activeResourceIds)}
        />
      ))}
    </SimpleGrid>
  </Box>
);

const CatalogIndex = () => {
  const catalogs = MF_ACCESS.useListAccessCatalogs();
  // Badges apps the member already holds active access to.
  const currentAccess = MF_ACCESS.useListAllCurrentAccess();
  const [search, setSearch] = useState("");
  const [catalogFilter, setCatalogFilter] = useState<string | null>(null);
  const [request, setRequest] = useState<RequestTarget | null>(null);

  // Catalogs (optionally narrowed to one) with their items filtered by the
  // search term; sections with no matching items drop out.
  const sections = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalogs
      .filter((c) => !catalogFilter || c.id === catalogFilter)
      .map((c) => ({
        catalog: c,
        items: q
          ? c.items.filter(
              (it) =>
                it.name.toLowerCase().includes(q) ||
                it.description?.toLowerCase().includes(q),
            )
          : c.items,
      }))
      .filter(({ items }) => items.length > 0);
  }, [catalogs, catalogFilter, search]);

  const activeResourceIds = useMemo(
    () => activeAccessResourceIds(currentAccess),
    [currentAccess],
  );

  return (
    <Box maxW="1440px" mx="auto" w="full">
      <Heading
        as="h1"
        fontFamily="heading"
        fontWeight={500}
        fontSize="3xl"
        letterSpacing="-0.01em"
        lineHeight="1"
        color="neutral.950"
        py="lg"
      >
        Catalog
      </Heading>

      {/* Search + catalog filter */}
      <Flex align="center" gap="sm" flexWrap="wrap" mb="2xl">
        <Box flex="1 1 320px" minW="240px" maxW="560px">
          <SearchInput value={search} onChange={setSearch} placeholder="Search…" />
        </Box>
        <FilterDropdown
          icon={<Icon as={TbBook2} boxSize="16px" />}
          label="Catalog"
          allLabel="All"
          value={catalogFilter}
          options={catalogs.map((c) => ({ id: c.id, label: c.name }))}
          onChange={setCatalogFilter}
        />
      </Flex>

      {sections.length === 0 ? (
        <VStack spacing="sm" color="neutral.600" fontSize="md" textAlign="center" py="3xl">
          <Icon as={TbSearchOff} boxSize="28px" color="neutral.400" />
          <Box>No catalog items match your search.</Box>
        </VStack>
      ) : (
        <VStack align="stretch" spacing="3xl">
          {sections.map(({ catalog, items }) => (
            <CatalogSection
              key={catalog.id}
              catalog={catalog}
              items={items}
              onRequest={setRequest}
              activeResourceIds={activeResourceIds}
            />
          ))}
        </VStack>
      )}

      {/* Mount only while open so the basket resets between items — a
          kept-mounted modal would carry the previous item into the next
          request and submit unintended access. */}
      {request && (
        <RequestAccessModal
          isOpen
          onClose={() => setRequest(null)}
          initialCatalogId={request.catalogId ?? null}
          initialCatalogItemId={request.itemId ?? null}
        />
      )}
    </Box>
  );
};

export default CatalogIndex;
